/**
 * Closed value sets of module 80 (spec 80 §4), shared by API, worker and web.
 * They mirror the PostgreSQL enums of migration 0009; adding a value means a
 * new migration plus an entry here.
 */
export const AI_JOB_TYPES = [
    'noop',
    'media_tagger',
    'topic_planner',
    'copywriter',
    'translator',
    'media_selector',
    'reviewer',
    'reviser',
    'art_director',
    'storyboarder',
    'content_director',
    // Migration 0092 (S140-7): a generated image through the license platform gateway.
    'image_generator',
    // Migration 0097 (S150-5): the year plan, contributed by module 110 (spec 110 section 4.7).
    'year_planner',
    // Migration 0102 (S140-4): an instrumental track through the license platform gateway.
    'music_composer',
    // Migration 0105 (S160-7): master data, knowledge, tone, pillars and photos proposed from the business's website.
    'website_reader',
    // Migration 0110 (S200-1): writing style and sample proposals from the tenant's own published posts.
    'feed_analyst',
    // Migration 0113 (S200-5): style lessons proposed from how people edited AI-written texts.
    'style_learner',
    // Migration 0114 (S200-1): brand colour, motif and template proposals from the images of chosen own posts (opt-in).
    'feed_visual_analyst',
    // Migration 0137 (E300 S300-3): the planning conversation's runs, contributed by module 110 (spec 110 section 4.10).
    'campaign_planner',
    // Migration 0158 (E540 S540-7): a property's reel video style, contributed by module 100 (spec 100 section 4.11).
    'reel_stylist',
];
export const AI_PROVIDERS = ['anthropic', 'openai'];
export const AI_RUN_STATUSES = ['queued', 'running', 'succeeded', 'failed', 'rejected_budget'];
export const AI_TERMINAL_RUN_STATUSES = ['succeeded', 'failed', 'rejected_budget'];
export const AI_SUBJECT_TYPES = ['content_item', 'media_asset', 'property'];
export const AI_CHAIN_STATUSES = ['running', 'succeeded', 'failed', 'rejected_budget'];
export const AI_CHAIN_STEP_STATUSES = ['pending', 'running', 'succeeded', 'failed', 'skipped', 'rejected_budget'];
/**
 * What a chain step does (spec 80 §4.8): an AI job, the render wait `render`
 * (S90-8: waits until module 90 reports every design render of the current
 * version `done`; no run, no provider call) or the final `submit`
 * (`draft -> in_review` by the chain's system actor; no run, no provider call).
 * Stored in `ai_chains.steps` (jsonb), so no enum migration is involved.
 */
export const AI_CHAIN_SUBMIT_STEP = 'submit';
export const AI_CHAIN_RENDER_STEP = 'render';
export const AI_CHAIN_STEP_KINDS = [...AI_JOB_TYPES, AI_CHAIN_RENDER_STEP, AI_CHAIN_SUBMIT_STEP];
/** The `origin` of a chain that prepares one added format (E320 S320-3, spec 80 section 4.20): no reviewer, no submit. */
export const VARIANT_PREPARE_ORIGIN = 'variant_prepare';
/** Model classes of the registry; `packages/config/ai/models.json` maps them to model IDs per provider. */
export const AI_MODEL_CLASSES = ['strong', 'medium', 'cheap'];
/**
 * AI quality tiers a tenant chooses (spec 80 section 4.9, REQ-KI-014): the
 * value set of the `ai_tier` enum of migration 0090.
 */
export const AI_TIERS = ['basic', 'better', 'best'];
export const AI_DEFAULT_TIER = 'better';
const MODEL_CLASS_LADDER = ['cheap', 'medium', 'strong'];
const TIER_STEP = { basic: -1, better: 0, best: 1 };
/**
 * The model class a job runs with in a tier, until the license platform
 * gateway maps tiers to models (REQ-KI-015, S140-2): `better` keeps the job's
 * own class, `basic` goes one class down, `best` one up, within cheap..strong.
 */
export function modelClassForTier(jobClass, tier) {
    const index = MODEL_CLASS_LADDER.indexOf(jobClass) + TIER_STEP[tier];
    return MODEL_CLASS_LADDER[Math.min(MODEL_CLASS_LADDER.length - 1, Math.max(0, index))];
}
/** `ai_runs.error_code` values (spec 80 §4.2); the client maps them to texts. */
export const AI_RUN_ERROR_CODES = [
    'schema_validation_failed',
    'provider_unavailable',
    'provider_rejected',
    'provider_not_configured',
    'content_changed',
    'glossary_violation',
    'text_too_long',
    'insufficient_media',
    'model_not_priced',
    'ai-budget-exhausted',
    'ai-budget-not-set',
    // The license platform refused the call for lack of credits (gateway, spec 80 section 4.10).
    'credits-exhausted',
    // A generated image could not be stored in the media library (S140-7).
    'media_ingest_failed',
    'timeout',
    'interrupted',
    'enqueue_failed',
    'job_not_registered',
    'prompt_version_unknown',
    'preparation_failed',
    // A multi-turn job asked for more tool calls than its limit (content director, spec 110 AC-110-015).
    'tool_loop_limit',
    // The campaign planner asked for a tool it does not have (E300 S300-3, AC-80-111).
    'tool_not_allowed',
    // The website import read no usable page (unreachable, refused, robots.txt, not HTML; S160-7).
    'website_unreadable',
    // The feed analysis found fewer than five usable own posts (S200-1).
    'too_few_posts',
    // The feed analysis could read none of the property's accounts (S200-1); the run output lists the account error codes.
    'feed_unreadable',
    // Bennows suspended the tenant; queued and scheduled runs end without a provider call (spec 140 REQ-140-021).
    'tenant_suspended',
];
/** Name of the BullMQ job on queue `ai`; its job ID is the run ID (spec 80 §4.6). */
export const AI_RUN_JOB_NAME = 'ai_run';
/**
 * The monthly repeatable job on queue `ai` (spec 80 section 4.16, S200-5): on
 * the 1st of the month at 05:00 UTC it queues a `style_learner` run per
 * property with enough new edit pairs. Origin of those runs: `style_lessons_sweep`.
 */
export const STYLE_LESSONS_SWEEP_JOB_NAME = 'style-lessons-sweep';
export const STYLE_LESSONS_SWEEP_CRON = '0 5 1 * *';
export const STYLE_LESSONS_SWEEP_ORIGIN = 'style_lessons_sweep';
//# sourceMappingURL=job-types.js.map